import React from 'react'
import { Trash2 } from 'lucide-react'

type Details = {
	id: string
	key: string
	value: string
	textValue: string
	numericValue: string
	currencyValue: string
	dateValue: string
	description: string
	detailType: string
	projectid: string
}

export interface Props {
	details?: Details[]
	handleEditDetail: Function
	handleDeleteDetail: Function
	title?: string
}

const formatCurrency = (value: string) => {
	const num = Number(value)
	if (isNaN(num)) return value
	return '$' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 0 }).format(num)
}

const formatDate = (value: string) => {
	try {
		const d = new Date(value)
		if (isNaN(d.getTime())) return value
		return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })
	} catch {
		return value
	}
}

const formatValue = (detail: Details) => {
	if (detail.detailType === 'Currency') return formatCurrency(detail.value?.replace('$', ''))
	if (detail.detailType === 'Date') return formatDate(detail.value)
	return detail.value
}

const DetailsTable = ({ details = [], handleEditDetail, handleDeleteDetail, title }: Props) => {
	if (details.length === 0) return null

	return (
		<div className="border border-gray-200 rounded mb-3 overflow-hidden">
			{/* Section title */}
			{title && (
				<div className="text-xs font-semibold text-[#0080fe] uppercase tracking-wider bg-[#0080fe]/10 px-3 py-1.5">
					{title}
				</div>
			)}

			{/* Data rows */}
			{details.map(detail => (
				<div
					key={detail.id}
					className="group grid grid-cols-[1fr_120px_24px_1fr_32px] gap-1.5 px-3 py-1 items-center border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
					onClick={() => handleEditDetail(detail.id)}
				>
					<span className="text-xs text-gray-800 truncate">{detail.key}</span>
					<span className="text-xs text-gray-800 text-right truncate">{formatValue(detail)}</span>
					<span />
					<span className="text-xs text-gray-500 truncate">{detail.description}</span>
					<div className="flex items-center justify-center">
						<button
							type="button"
							title="Delete detail"
							onClick={e => {
								e.stopPropagation()
								handleDeleteDetail(detail)
							}}
							className="opacity-0 group-hover:opacity-100 bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-colors p-0"
						>
							<Trash2 className="w-3.5 h-3.5" />
						</button>
					</div>
				</div>
			))}
		</div>
	)
}

export default DetailsTable
